import React from 'react';
import { getPurposeStyle } from '@/shared/utils/constants';
import ComingSoonCell from './ComingSoonCell';

interface CellRendererParams {
  value: string[] | null | undefined;
  context?: {
    language?: 'fa' | 'en';
    isDark?: boolean;
  };
}

const PurposeCellRenderer = (params: CellRendererParams) => {
  const value = params.value; // Array of purpose strings
  const language = params.context?.language || 'fa';
  const isDark = params.context?.isDark || document.documentElement.classList.contains('dark');
  
  if (!value || !Array.isArray(value) || value.length === 0) {
    return <ComingSoonCell language={language} isDark={isDark} />;
  }

  // Generate tooltip HTML content with dark mode support
  const bgColor = isDark ? '#ffffff' : 'rgb(31, 41, 55)';
  const textColor = isDark ? 'rgb(31, 41, 55)' : 'white';
  const borderColor = isDark ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)';

  const purposesHTML = value.map((purposeType, index) => {
    const purposeConfig = getPurposeStyle(purposeType);
    if (!purposeConfig) return '';
    
    const label = language === 'en' ? purposeConfig.enLabel : purposeConfig.label;
    const description = language === 'en' ? purposeConfig.description.en : purposeConfig.description.fa;
    
    return `
      <div style="margin-bottom: ${index < value.length - 1 ? '12px' : '0'};">
        <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
          <span style="font-size: 18px;">${purposeConfig.icon}</span>
          <span style="color: ${textColor}; font-weight: 500; font-size: 14px;">${label}</span>
        </div>
        <div style="color: ${isDark ? 'rgb(107, 114, 128)' : 'rgb(209, 213, 219)'}; font-size: 11px; padding-right: 26px; line-height: 1.4;">
          ${description}
        </div>
      </div>
    `;
  }).join('');

  const tooltipHTML = `
    <div style="background-color: ${bgColor}; color: ${textColor}; padding: 12px; border-radius: 8px; border: 1px solid ${borderColor}; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); min-width: 150px;">
      <div style="font-weight: 600; margin-bottom: 8px; font-size: 13px; color: ${isDark ? 'rgb(107, 114, 128)' : 'rgb(209, 213, 219)'}; text-transform: uppercase; letter-spacing: 0.5px;">
        ${language === 'fa' ? 'هدف' : 'Purpose'}
      </div>
      ${purposesHTML}
    </div>
  `;

  const handleMouseEnter = (e: React.MouseEvent<HTMLSpanElement>) => {
    const currentIsDark = document.documentElement.classList.contains('dark');
    const updatedBgColor = currentIsDark ? '#ffffff' : 'rgb(31, 41, 55)';
    const updatedTextColor = currentIsDark ? 'rgb(31, 41, 55)' : 'white';
    const updatedBorderColor = currentIsDark ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)';
    
    const updatedPurposesHTML = value.map((purposeType, index) => {
      const purposeConfig = getPurposeStyle(purposeType);
      if (!purposeConfig) return '';
      
      const label = language === 'en' ? purposeConfig.enLabel : purposeConfig.label;
      const description = language === 'en' ? purposeConfig.description.en : purposeConfig.description.fa;
      
      return `
        <div style="margin-bottom: ${index < value.length - 1 ? '12px' : '0'};">
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
            <span style="font-size: 18px;">${purposeConfig.icon}</span>
            <span style="color: ${updatedTextColor}; font-weight: 500; font-size: 14px;">${label}</span>
          </div>
          <div style="color: ${currentIsDark ? 'rgb(107, 114, 128)' : 'rgb(209, 213, 219)'}; font-size: 11px; padding-right: 26px; line-height: 1.4;">
            ${description}
          </div>
        </div>
      `;
    }).join('');

    const updatedTooltipHTML = `
      <div style="background-color: ${updatedBgColor}; color: ${updatedTextColor}; padding: 12px; border-radius: 8px; border: 1px solid ${updatedBorderColor}; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); min-width: 150px;">
        <div style="font-weight: 600; margin-bottom: 8px; font-size: 13px; color: ${currentIsDark ? 'rgb(107, 114, 128)' : 'rgb(209, 213, 219)'}; text-transform: uppercase; letter-spacing: 0.5px;">
          ${language === 'fa' ? 'هدف' : 'Purpose'}
        </div>
        ${updatedPurposesHTML}
      </div>
    `;
    
    e.currentTarget.setAttribute('data-tooltip-html', updatedTooltipHTML);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', paddingTop: '8px', paddingBottom: '8px' }}>
      {value.map((purposeType, index) => {
        const purposeConfig = getPurposeStyle(purposeType);
        if (!purposeConfig) return null;
        
        return (
          <span
            key={`${purposeType}-${index}`}
            data-tooltip-id="purpose-tooltip-global"
            data-tooltip-place="top"
            data-tooltip-html={tooltipHTML}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: purposeConfig.bgColor,
              color: purposeConfig.color,
              borderRadius: '50%',
              width: '28px',
              height: '28px',
              fontSize: '14px',
              cursor: 'pointer',
              paddingTop: '5px',
              lineHeight: '1',
            }}
            onMouseEnter={handleMouseEnter}
          >
            {purposeConfig.icon}
          </span>
        );
      })}
    </div>
  );
};

export default PurposeCellRenderer;


